import { useTranslation } from '@/hooks/useTranslation';
import { Check } from 'lucide-react';

interface LanguageSwitcherProps {
  variant?: 'compact' | 'cards';
}

const FLAG_URLS: Record<string, string> = {
  en: 'https://flagcdn.com/us.svg',
  fr: 'https://flagcdn.com/fr.svg',
};

export function LanguageSwitcher({ variant = 'compact' }: LanguageSwitcherProps) {
  const { language, setLanguage } = useTranslation();

  const languages = [
    { code: 'en', name: 'English', desc: 'American English' },
    { code: 'fr', name: 'Français', desc: 'French' },
  ] as const;

  const FlagImg = ({ code, size = 20 }: { code: string; size?: number }) => (
    <img
      src={FLAG_URLS[code]}
      alt={code.toUpperCase()}
      className="inline-block rounded-sm object-cover"
      style={{ width: size, height: Math.round(size * 0.75) }}
    />
  );

  // Compact version for header/navbar
  if (variant === 'compact') {
    return (
      <button
        onClick={() => setLanguage(language === 'en' ? 'fr' : 'en')}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-800/50 hover:bg-gray-200 dark:hover:bg-gray-700/70 border border-gray-100 dark:border-gray-700/50 transition-all duration-200"
      >
        <FlagImg code={language} />
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {language.toUpperCase()}
        </span>
      </button>
    );
  }

  // Cards version for settings page
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {languages.map((lang) => (
        <button
          key={lang.code}
          onClick={() => setLanguage(lang.code)}
          className={`p-5 rounded-xl border-2 transition-all duration-200 text-left relative ${
            language === lang.code
              ? 'border-[#16c47f] bg-[#16c47f]/10'
              : 'border-gray-100 dark:border-gray-700/50 bg-white dark:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-500'
          }`}
        >
          {language === lang.code && (
            <div className="absolute top-3 right-3 w-5 h-5 bg-[#16c47f] rounded-full flex items-center justify-center">
              <Check className="w-3 h-3 text-white" />
            </div>
          )}
          <FlagImg code={lang.code} size={32} />
          <p className="text-gray-900 dark:text-white font-semibold text-sm mt-3">{lang.name}</p>
          <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">{lang.desc}</p>
        </button>
      ))}
    </div>
  );
}
